import { UserProfile } from "@/__generated__/graphql";
import { BranchPicker } from "@/components/modals/BranchPicker/BranchPicker";
import {
  CalendarMonth,
  CardGiftcard,
  ConfirmationNumber,
  ContactSupport,
  Handshake,
  KeyboardArrowRight,
  LocalOffer,
  Login,
  MenuBook,
  OpenInNew,
  Person,
} from "@mui/icons-material";
import { Box, List, ListItem, ListItemButton, ListItemContent, Stack, Typography } from "@mui/joy";
import React from "react";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";

const SettingsMenuItem = ({
  title,
  icon,
  href,
  onClick,
  targetBlank,
  divider,
}: {
  title: React.ReactNode;
  icon: React.ReactNode;
  targetBlank?: boolean;
  divider?: boolean;
} & (
  | {
      href: string;
      onClick?: undefined;
    }
  | {
      onClick: () => void;
      href?: undefined;
    }
)) => {
  return (
    <ListItem
      sx={{
        p: 0,
        ...(divider && {
          borderBottom: "1px solid",
          borderColor: "neutral.200",
        }),
      }}
    >
      <ListItemButton
        {...(onClick
          ? { onClick }
          : {
              component: Link,
              to: href,
              target: targetBlank ? "_blank" : undefined,
              rel: targetBlank ? "noopener noreferrer" : undefined,
            })}
        sx={{
          minHeight: 56,
          mx: 0,
          gap: 1.25,
          px: 1.25,
          py: 0.75,
          borderRadius: 0,
          "&:hover": { backgroundColor: "neutral.50" },
          "&:active": { backgroundColor: "neutral.100" },
        }}
      >
        <Box
          sx={{
            display: "grid",
            width: 36,
            height: 36,
            flex: "0 0 auto",
            placeItems: "center",
            borderRadius: "md",
            color: "primary.softColor",
            backgroundColor: "primary.softBg",
          }}
        >
          {icon}
        </Box>
        <ListItemContent sx={{ minWidth: 0 }}>
          <Typography
            level="body-md"
            fontWeight="md"
            sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {title}
          </Typography>
        </ListItemContent>
        {targetBlank ? (
          <OpenInNew sx={{ flex: "0 0 auto", fontSize: 20, color: "neutral.400" }} />
        ) : (
          <KeyboardArrowRight sx={{ flex: "0 0 auto", fontSize: 24, color: "neutral.400" }} />
        )}
      </ListItemButton>
    </ListItem>
  );
};

const SettingsMenuSection = ({
  title,
  children,
}: React.PropsWithChildren<{
  title: React.ReactNode;
}>) => (
  <Stack component="section" gap={0.75}>
    <Typography component="h2" level="title-sm" sx={{ px: 0.5, color: "neutral.600" }}>
      {title}
    </Typography>
    <Box
      sx={{
        overflow: "hidden",
        border: "1px solid",
        borderColor: "neutral.200",
        borderRadius: "lg",
        backgroundColor: "background.surface",
      }}
    >
      <List size="lg" sx={{ p: 0 }}>
        {children}
      </List>
    </Box>
  </Stack>
);

export default function SettingsMenuView({ user }: { user?: UserProfile | null }) {
  const fullName = user ? [user.name, user.surname].filter(Boolean).join(" ") : "";

  return (
    <Stack gap={3} sx={{ px: 2, pt: 2, pb: 12 }}>
      <SettingsMenuSection title={<FormattedMessage defaultMessage="Аккаунт" />}>
        {user ? (
          <>
            <SettingsMenuItem
              title={fullName || <FormattedMessage defaultMessage="Профиль" />}
              icon={<Person fontSize="small" />}
              href="/profile/edit"
              divider
            />
            <SettingsMenuItem
              title={<FormattedMessage defaultMessage="Мои занятия" />}
              icon={<CalendarMonth fontSize="small" />}
              href="/profile/lessons"
              divider
            />
            <SettingsMenuItem
              title={<FormattedMessage defaultMessage="Мои абонементы" />}
              icon={<ConfirmationNumber fontSize="small" />}
              href="/profile/passes"
            />
          </>
        ) : (
          <SettingsMenuItem
            title={<FormattedMessage defaultMessage="Войти в аккаунт" />}
            icon={<Login fontSize="small" />}
            href="/home"
          />
        )}
      </SettingsMenuSection>

      <SettingsMenuSection title={<FormattedMessage defaultMessage="AMANI" />}>
        {user && (
          <>
            <SettingsMenuItem
              title={<FormattedMessage defaultMessage="Подарочный сертификат" />}
              icon={<CardGiftcard fontSize="small" />}
              href="https://amani.by/gift-card"
              targetBlank
              divider
            />
            <SettingsMenuItem
              title={<FormattedMessage defaultMessage="Партнеры AMANI" />}
              icon={<Handshake fontSize="small" />}
              href="/partners"
              divider
            />
            {user?.dateRecognition && (
              <SettingsMenuItem
                title={<FormattedMessage defaultMessage="Прайс-лист" />}
                icon={<LocalOffer fontSize="small" />}
                href="/price.pdf?v=2026-09"
                targetBlank
                divider
              />
            )}
          </>
        )}
        <SettingsMenuItem
          title={<FormattedMessage defaultMessage="Правила школы" />}
          icon={<MenuBook fontSize="small" />}
          href="/settings/rules"
        />
      </SettingsMenuSection>

      <SettingsMenuSection title={<FormattedMessage defaultMessage="Помощь" />}>
        <BranchPicker>
          {({ onOpen }) => (
            <SettingsMenuItem
              title={<FormattedMessage defaultMessage="Связаться с нами" />}
              icon={<ContactSupport fontSize="small" />}
              onClick={onOpen}
            />
          )}
        </BranchPicker>
      </SettingsMenuSection>
    </Stack>
  );
}
